import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Wallet,
  ArrowUp,
  ChevronLeft,
  ChevronRight,
  UserRound,
  Circle,
} from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { cn, dateNow, shorten, showTimeDiffPartial } from "@/lib/utils";
import Countdown from "@/components/countdown";
import { ConnectButton } from "@/components/connect-button";
import { useWallet } from "@txnlab/use-wallet";
import {
  TransactionDialog,
  transactionInner,
} from "@/components/transaction/transaction-dialog";
import { SingleToken } from "@/components/single-token";
import { z } from "zod";
import PercentageSelector from "./percentage-selector";
import { algodNetwork } from "@/lib/algo";
import { toLocaleString } from "@/lib/utils";
import { useAlgoAcctInfo, useBalance } from "@/hooks/use-algo-account";
import { decodeTransactions, getBenchInstanceClient } from "@/lib/clients";
import { BenchStateFull } from "@myth-finance/bench-sdk";
import { useSearchParams } from "react-router-dom";

const BOX_FEES_MICRO = 2500 + 400 * 40;
const BOX_FEES_ALGO = BOX_FEES_MICRO / 1e6;

interface MakeAccountSchemaArgs {
  tokenSymbol: string;
  paymentTokenSymbol: string;
  remaining?: number;
  price: number;
  userMaxSpend: number;
  paymentTokenUserBalance?: number;
  connected?: boolean;
}
function makeAmountSchema({
  tokenSymbol,
  paymentTokenSymbol,
  remaining,
  price,
  userMaxSpend,
  paymentTokenUserBalance,
  connected,
}: MakeAccountSchemaArgs) {
  return z
    .string()
    .refine(
      (val) => {
        if (val === "") return true;
        const num = Number(val);
        return !isNaN(num) && num >= 0;
      },
      { message: "Please enter a valid positive amount" }
    )
    .refine(
      (val) => {
        if (val === "" || paymentTokenUserBalance === undefined) return true;
        const num = Number(val);
        return num <= paymentTokenUserBalance;
      },
      {
        message: connected
          ? `Not enough ${paymentTokenSymbol}`
          : `Wallet not connected`,
      }
    )
    .refine(
      (val) => {
        if (val === "" || !remaining) return true;
        const num = Number(val);
        return num / price <= remaining;
      },
      { message: `Not enough ${tokenSymbol} available in bench` }
    )
    .refine(
      (val) => {
        if (val === "" || !remaining) return true;
        const num = Number(val);
        return num <= userMaxSpend;
      },
      { message: `Account buy limit exceeded` }
    );
}

type AmountSchema = ReturnType<typeof makeAmountSchema>;

export function BuyToken({
  data,
  refetch,
}: {
  data: BenchStateFull;
  refetch: () => any;
  error?: string;
}) {
  const { activeAddress } = useWallet();

  const [amount, setAmount] = useState<string>("");
  const [paymentAmount, setPaymentAmount] = useState<string>("");
  const [amountSchema, setAmountSchema] = useState<AmountSchema>();

  const {
    sellingAssetId,
    purchaseAssetId,
    price,
    total,
    timeStart,
    timeEnd,
    remainingTokens,
    userMaxSpend,
    purchaseUnitName,
    sellingUnitName,
    purchaseDecimalDenom,
    userNeedMBR,
    purchaseLimit,
  } = data ?? {};

  const { data: accountInfo, refetch: refetchAlgoAcct } =
    useAlgoAcctInfo(activeAddress);

  const userSellingBalance = useBalance(sellingAssetId, accountInfo);
  const balance = useBalance(purchaseAssetId, accountInfo);

  const inputRef = useRef<HTMLInputElement>(null);

  const displaySellingAssetId =
    algodNetwork === "mainnet" ? sellingAssetId : 2994233666;

  const [showBuyForm, setShowBuyForm] = useState(false);
  const [validationError, setValidationError] = useState<string>();

  useEffect(() => {
    setAmountSchema(
      makeAmountSchema({
        tokenSymbol: sellingUnitName,
        paymentTokenSymbol: purchaseUnitName,
        paymentTokenUserBalance: balance,
        remaining: remainingTokens,
        userMaxSpend: userMaxSpend! / purchaseDecimalDenom,
        price: price!,
        connected: !!activeAddress,
      })
    );
  }, [balance, userMaxSpend, purchaseDecimalDenom, remainingTokens]);

  const isValidAmount =
    !!paymentAmount && !validationError && parseFloat(amount) > 0;
  const amountPreviewClass = isValidAmount ? "" : "text-muted-foreground";
  const needOptin = userSellingBalance === -1;

  const validateAmount = (value: string) => {
    if (amountSchema) {
      const result = amountSchema.safeParse(value);
      if (!result.success) {
        setValidationError(result.error.errors[0].message);
        return false;
      }
      setValidationError(undefined);
      return true;
    }
    return true;
  };

  const changeAmount = ({
    currentTarget: { value: paymentAmountStr },
  }: React.ChangeEvent<HTMLInputElement>) => {
    setPaymentAmount(paymentAmountStr);

    if (!validateAmount(paymentAmountStr)) {
      setAmount("");
      return;
    }

    if (paymentAmountStr !== "") {
      const paymentAmount = parseFloat(paymentAmountStr);
      const tokenAmount = paymentAmount / price!;
      setAmount(tokenAmount.toFixed(6));
    } else {
      setAmount("");
    }
  };

  const handlePercentageClick = (percentage: number) => {
    if (!activeAddress) {
      alert("Connect your wallet first");
      return;
    }
    if (balance === -1 || balance === undefined) {
      alert(`You are not opted in to ${purchaseUnitName}`);
      return;
    }
    if (balance === 0) {
      alert(`You do not have any ${purchaseUnitName}`);
      return;
    }
    const maxRequiredToBuy = remainingTokens! * price! * purchaseDecimalDenom;
    let paymentTokenAmount = (
      (Math.min(balance, userMaxSpend, maxRequiredToBuy) /
        purchaseDecimalDenom) *
      percentage
    ).toFixed(6);
    let desiredTokenAmount = parseFloat(paymentTokenAmount) / price!;
    if (desiredTokenAmount > remainingTokens!) {
      paymentTokenAmount = (remainingTokens! * price!).toFixed(6);
      desiredTokenAmount = remainingTokens!;
    }
    validateAmount(String(paymentTokenAmount));
    setPaymentAmount(paymentTokenAmount);
    setAmount(desiredTokenAmount.toFixed(6));
  };

  const handleClear = () => {
    setAmount("");
    setPaymentAmount("");
    setValidationError(undefined);
  };

  const onSuccess = () => {
    setAmount("");
    setPaymentAmount("");
    inputRef.current!.value = "";
    refetchAlgoAcct();
    setTimeout(() => refetch(), 500);
  };

  const makeBuyTransactions = async (amount: number) => {
    const etxns = await getBenchInstanceClient(
      data.appId,
      data.registryAppId
    ).makeBuyTxns({
      sender: activeAddress!,
      amount: BigInt(amount),
    });
    return decodeTransactions(etxns);
  };

  const [searchParams] = useSearchParams();

  // Get a single value
  const fakedown = searchParams.get("fakedown");
  const countdownOffset = useMemo(() => {
    if (fakedown) {
      const timeUntilStart = timeStart - Date.now() / 1000;
      const offset = timeUntilStart - Number(fakedown);
      return offset;
    }
  }, [fakedown]);

  if (timeStart && !showBuyForm) {
    return (
      <Card className="min-w-[381px] shadow-sm border-opacity-50 backdrop-blur-sm">
        <CardHeader className="pb-4">
          <div className="flex items-center justify-between">
            <div className="space-y-1">
              <CardTitle className="text-2xl font-bold text-foreground">
                Buy {sellingUnitName}
              </CardTitle>
              <CardDescription className="text-sm text-muted-foreground/70">
                Purchase {sellingUnitName} tokens with {purchaseUnitName}
              </CardDescription>
            </div>
            <SingleToken variant="md" assetId={displaySellingAssetId} />
          </div>
        </CardHeader>
        <CardContent className="flex flex-col gap-3">
          <div className="">This bench sale opens in </div>
          <Countdown
            targetDate={timeStart}
            offset={countdownOffset}
            onExpire={() => setShowBuyForm(true)}
          />
        </CardContent>
      </Card>
    );
  }

  if (timeEnd && timeEnd < dateNow()) {
    const displayState =
      timeEnd < timeStart
        ? "cancelled"
        : remainingTokens
        ? "closed"
        : "sold out";
    return (
      <Card className="min-w-[381px] shadow-lg mt-6 border-opacity-50 backdrop-blur-sm">
        <CardHeader className="pb-4">
          <div className="flex items-center justify-between">
            <div className="space-y-1">
              <CardTitle className="text-2xl font-bold text-foreground">
                {/* TODO calculate dust remaining as sold out */}
                Bench {displayState}
              </CardTitle>
              <CardDescription className="text-sm text-muted-foreground/70"></CardDescription>
            </div>
            <SingleToken variant="sm" assetId={displaySellingAssetId} />
          </div>
        </CardHeader>
        <CardContent className="flex flex-col mt-2 gap-2">
          <div className="flex justify-between px-1 text-sm">
            <span className="text-muted-foreground/60">Bench size</span>
            <span className="font-medium text-muted-foreground/90">
              {toLocaleString(total)} {sellingUnitName}
            </span>
          </div>
          {remainingTokens ? (
            <div className="flex justify-between px-1 text-sm">
              <span className="text-muted-foreground/60">Sold</span>
              <span className="font-medium text-muted-foreground/90">
                {toLocaleString(total! - remainingTokens)} {sellingUnitName}
              </span>
            </div>
          ) : null}
          <div className="flex justify-between px-1 text-sm">
            <span className="text-muted-foreground/60">Revenue</span>
            <span className="font-medium text-muted-foreground/90">
              {toLocaleString((total! - remainingTokens) * price)}{" "}
              {purchaseUnitName}
            </span>
          </div>
          <div className="flex justify-between px-1 text-sm">
            <span className="text-muted-foreground/60">Sale duration</span>
            <span className="font-medium text-muted-foreground/90">
              {timeEnd === 0 || timeEnd >= timeStart
                ? showTimeDiffPartial((timeEnd! - timeStart) * 1000)
                : "Cancelled"}
            </span>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card
      glowing="primary"
      className="w-[381px] border-opacity-50 backdrop-blur-sm"
    >
      <CardHeader className="pb-4">
        <div className="flex items-center justify-between">
          <div className="space-y-1">
            <CardTitle className="text-2xl font-bold text-foreground">
              Buy {sellingUnitName}
            </CardTitle>
            <CardDescription className="text-sm text-muted-foreground/70">
              Purchase {sellingUnitName} tokens with {purchaseUnitName}
            </CardDescription>
          </div>
          <SingleToken
            variant="md"
            className="-mr-2 -mt-2"
            assetId={displaySellingAssetId}
          />
        </div>
      </CardHeader>
      <CardContent className="pb-6">
        <form className="space-y-6" noValidate>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label
                className="flex justify-between items-end text-sm font-medium"
                htmlFor="buy_amount"
              >
                Amount to Pay
                {Number.isFinite(balance) ? (
                  <span
                    className={cn(
                      "text-muted-foreground/70 text-xs",
                      balance! === 0 ? "text-destructive" : ""
                    )}
                  >
                    {Number.isFinite(balance) && balance! > -1
                      ? toLocaleString(balance! / purchaseDecimalDenom)
                      : "-"}{" "}
                    {purchaseUnitName}
                  </span>
                ) : null}
              </Label>
              <div className="relative">
                <Input
                  id="buy_amount"
                  onChange={changeAmount}
                  value={paymentAmount}
                  ref={inputRef}
                  placeholder="0.00"
                  className={cn(
                    "pr-24 h-12 text-lg",
                    validationError &&
                      "border-destructive focus-visible:ring-destructive"
                  )}
                />
                {paymentAmount && (
                  <button
                    type="button"
                    onClick={handleClear}
                    className="absolute right-[5.5rem] top-1/2 -translate-y-1/2 w-6 h-6 flex items-center justify-center hover:bg-muted/50 rounded-full text-muted-foreground/70 hover:text-muted-foreground"
                  >
                    ×
                  </button>
                )}
                <div className="absolute right-3 top-1/2 -translate-y-1/2 text-sm font-medium text-muted-foreground/70">
                  {purchaseUnitName}
                </div>
              </div>
              {validationError && (
                <p className="text-sm text-destructive">{validationError}</p>
              )}
              <div className="flex gap-2 mt-2 justify-end">
                <PercentageSelector
                  remainingTokens={remainingTokens!}
                  userMaxSpend={userMaxSpend!}
                  tokenPrice={price!}
                  userPurchaseAssetBalance={balance!}
                  onPercentageClick={handlePercentageClick}
                  purchaseAssetDecimalDenom={purchaseDecimalDenom}
                />
              </div>
            </div>

            <div className="relative space-y-3">
              <div
                className={`bg-muted/30 rounded-lg p-3 ${amountPreviewClass}`}
              >
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-muted-foreground/90">
                    You Pay
                  </span>
                  <div className="flex items-center gap-2">
                    <span className="text-lg font-semibold text-foreground">
                      {isValidAmount && !validationError
                        ? toLocaleString(parseFloat(paymentAmount))
                        : "0.00"}
                    </span>
                    <span className="text-sm font-medium text-muted-foreground/70">
                      {purchaseUnitName}
                    </span>
                  </div>
                </div>
              </div>

              <div className="flex justify-center">
                <div className="bg-background rounded-full p-1 -my-2 z-10">
                  <ArrowUp
                    size={16}
                    className="text-muted-foreground/50 rotate-180"
                  />
                </div>
              </div>

              <div className={`bg-muted/30 rounded-lg p-3`}>
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-muted-foreground/90">
                    You Receive
                  </span>
                  <div className="flex items-center gap-2">
                    <span className="text-lg font-semibold text-foreground">
                      {isValidAmount && !validationError
                        ? toLocaleString(parseFloat(amount))
                        : "0.00"}
                    </span>
                    <span className="text-sm font-medium text-muted-foreground/70">
                      {sellingUnitName}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="flex flex-col gap-1">
            <div className="flex justify-between px-1 text-sm">
              <span className="text-muted-foreground/60">Price per token</span>
              <span className="font-medium text-muted-foreground/90">
                {price} {purchaseUnitName}
              </span>
            </div>
            {purchaseLimit > 0 ? (
              <div className="flex justify-between px-1 text-sm">
                <span
                  className={cn(
                    "text-muted-foreground/60",
                    userMaxSpend === 0 ? "text-destructive/85" : ""
                  )}
                >
                  Account limit
                </span>
                <span
                  className={cn(
                    "font-medium text-muted-foreground/90",
                    userMaxSpend === 0 ? "text-destructive" : ""
                  )}
                >
                  {toLocaleString(userMaxSpend / purchaseDecimalDenom)}{" "}
                  {purchaseUnitName}
                </span>
              </div>
            ) : null}
            <div className="flex justify-between px-1 text-sm">
              <span className="text-muted-foreground/60">Total</span>
              <span className="font-medium text-muted-foreground/90">
                {total ? toLocaleString(total) : "-"} {sellingUnitName}
              </span>
            </div>
            <div className="flex justify-between px-1 text-sm">
              <span className="text-muted-foreground/60">Remaining</span>
              <span className="font-medium text-muted-foreground/90">
                {remainingTokens ? toLocaleString(remainingTokens) : "-"}{" "}
                {sellingUnitName}
              </span>
            </div>
          </div>
        </form>
      </CardContent>
      <CardFooter className="flex justify-center pb-6">
        <div className="relative w-full">
          {isValidAmount && (
            <div
              className={cn(
                "absolute inset-0 rounded-lg",
                "shadow-[0_0_25px_8px] shadow-primary/50",
                "animate-[pulse_5s_linear_infinite]"
              )}
            />
          )}
          <TransactionDialog
            type="purchase"
            trigger={(onClick) => (
              <ConnectButton
                className={cn(
                  "inline-flex w-full items-center justify-center rounded-lg text-sm font-medium",
                  "ring-offset-background transition-all",
                  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
                  "disabled:pointer-events-none disabled:opacity-50",
                  "bg-primary text-primary-foreground hover:bg-primary/90",
                  "h-12 px-6 py-2",
                  "relative"
                )}
                disabled={!isValidAmount}
                onClick={onClick}
              >
                <Wallet size={18} className="mr-1.5" />
                <span>Buy with {purchaseUnitName}</span>
              </ConnectButton>
            )}
            transactionBuilder={() =>
              makeBuyTransactions(
                Math.floor(parseFloat(paymentAmount) * purchaseDecimalDenom)
              )
            }
            title={`Purchasing ${sellingUnitName}`}
            onClose={() => refetch()}
            onSuccess={onSuccess}
          >
            {transactionInner(
              "purchase",
              <>
                You are going to{" "}
                <strong>
                  buy {toLocaleString(parseFloat(amount))} {sellingUnitName}
                </strong>
              </>,
              <>
                <div className="flex justify-between gap-2 mb-2">
                  <div className="flex items-center">
                    <UserRound size={18} className="mr-1" /> Account
                  </div>{" "}
                  <span>{shorten(activeAddress!)}</span>
                </div>
                {needOptin ? (
                  <div className="flex justify-between gap-2 text-foreground">
                    <div className="flex items-center">
                      <Circle size={18} className="mr-1" /> Opting in to
                    </div>
                    <span>{sellingUnitName}</span>
                  </div>
                ) : null}
                <div className="flex justify-between gap-2 text-foreground">
                  <div className="flex items-center">
                    <ChevronRight size={18} className="mr-1" /> Paying
                  </div>
                  <span className="text-right">
                    {toLocaleString(parseFloat(paymentAmount))}{" "}
                    {purchaseUnitName}
                  </span>
                </div>
                {userNeedMBR ? (
                  <div className="flex justify-between gap-2 text-foreground">
                    <div className="flex items-center">
                      <ChevronRight size={18} className="mr-1" /> Storage fees
                    </div>
                    <span className="text-right">{BOX_FEES_ALGO} ALGO</span>
                  </div>
                ) : null}
                <div className="flex justify-between gap-2 mt-2">
                  <div className="flex items-center">
                    <ChevronLeft size={18} className="mr-1" /> Receiving
                  </div>{" "}
                  <span className="text-right">
                    {toLocaleString(parseFloat(amount))} {sellingUnitName}
                  </span>
                </div>
              </>
            )}
          </TransactionDialog>
        </div>
      </CardFooter>
    </Card>
  );
}
